import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const Login21 = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        navigate('/template21');
    };

    const inputBaseClass = "w-full px-4 py-3.5 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F0EB] text-sm rounded outline-none transition-colors duration-300 focus:border-[#C9A96E] placeholder-[#555]";

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main className="pt-20 min-h-screen flex">
                    {/* Left Side - Hero Image (Desktop Only) */}
                    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=1920"
                            alt="Exclusive fashion"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/40 via-[#0A0A0A]/60 to-[#0A0A0A]" />
                        <div className="relative z-10 flex flex-col justify-center items-start p-16 max-w-lg">
                            <div className="w-12 h-px bg-[#C9A96E] mb-6" />
                            <h2
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-4xl xl:text-5xl font-light text-[#C9A96E] leading-tight mb-4"
                            >
                                Exclusive Collections Await
                            </h2>
                            <p className="text-[#F5F0EB]/70 text-base leading-relaxed">
                                Step into a world of dark elegance and timeless Indian couture crafted for the modern connoisseur.
                            </p>
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className="flex-1 flex items-center justify-center px-6 py-12 lg:py-0">
                        <div className="w-full max-w-md">
                            {/* Brand */}
                            <div className="text-center mb-10">
                                <h1
                                    onClick={() => navigate('/template21')}
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-3xl font-semibold text-[#C9A96E] cursor-pointer tracking-[4px] mb-3"
                                >
                                    NOIR
                                </h1>
                                <h2
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-2xl lg:text-3xl font-light text-[#C9A96E] tracking-wide"
                                >
                                    Welcome Back
                                </h2>
                                <p className="text-[#8A8A7A] text-sm mt-2">
                                    Sign in to access your account and exclusive collections
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-[#F5F0EB] text-sm font-medium mb-2">Email</label>
                                    <div className="relative">
                                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A7A]" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="you@example.com"
                                            className={`${inputBaseClass} pl-12`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[#F5F0EB] text-sm font-medium mb-2">Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A7A]" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your password"
                                            className={`${inputBaseClass} pl-12 pr-12`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A8A7A] hover:text-[#C9A96E] transition-colors"
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-[#2A2A2A] bg-[#1A1A1A] accent-[#C9A96E]"
                                        />
                                        <span className="text-[#8A8A7A] text-sm">Remember me</span>
                                    </label>
                                    <button
                                        type="button"
                                        className="text-[#8A8A7A] text-sm hover:text-[#C9A96E] transition-colors"
                                    >
                                        Forgot password?
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-4 bg-[#C9A96E] text-[#0A0A0A] text-sm font-semibold tracking-wider uppercase hover:bg-[#D4B87A] transition-colors duration-300"
                                >
                                    Sign In
                                </button>
                            </form>

                            {/* Divider */}
                            <div className="flex items-center my-8">
                                <div className="flex-1 h-px bg-[#2A2A2A]" />
                                <span className="px-4 text-[#8A8A7A] text-xs tracking-wider uppercase">or</span>
                                <div className="flex-1 h-px bg-[#2A2A2A]" />
                            </div>

                            {/* Google Login */}
                            <button className="w-full flex items-center justify-center gap-3 px-6 py-3.5 border border-[#2A2A2A] text-[#F5F0EB] text-sm rounded hover:border-[#C9A96E] transition-colors duration-300">
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </button>

                            {/* Sign Up Link */}
                            <p className="text-center mt-8 text-[#8A8A7A] text-sm">
                                Don't have an account?{' '}
                                <button
                                    onClick={() => navigate('/template21/signup')}
                                    className="text-[#C9A96E] font-medium hover:text-[#D4B87A] transition-colors"
                                >
                                    Create one
                                </button>
                            </p>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Login21;
